import { SHARED_WALLET_GUID } from './constants'
import {
  IAccountFuel,
  IFuelBalance,
  INozzle,
  IRefuelAccount,
  IRefuelWallet,
  IStation,
} from './types'

/** `Широта` / `Долгота` are plain numbers. Zero is no position, not the equator. */
const readCoordinate = (value: any): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value !== 0
    ? value
    : undefined

export const stationsAdapter = (stations: any[]): IStation[] =>
  (stations || []).map(
    ({ Код, Адрес, Наименование, АЗКГуид, Широта, Долгота }) => ({
      code: String(Код ?? ''),
      address: Адрес || '',
      name: Наименование || '',
      guid: АЗКГуид || '',
      search: `${Адрес || ''} ${Наименование || ''} ${Код ?? ''}`.toLowerCase(),
      lat: readCoordinate(Широта),
      lng: readCoordinate(Долгота),
    })
  )

/**
 * `Result.ОстаткиПоАккаунтам` of Cabinet/FuelData — one row per account × fuel,
 * grouped into one entry per account so it can drive the account Select.
 */
export const accountsAdapter = (balances: any[]): IRefuelAccount[] => {
  const accounts: IRefuelAccount[] = []
  const byId: { [id: string]: IRefuelAccount } = {}

  ;(balances || []).forEach(
    ({ АккаунтУИД, Аккаунт, НоменклатураУИД, Номенклатура, Количество }) => {
      const id = АккаунтУИД || ''
      if (!byId[id]) {
        byId[id] = { id, name: Аккаунт || '', fuels: [] }
        accounts.push(byId[id])
      }
      byId[id].fuels.push({
        id: НоменклатураУИД || '',
        name: Номенклатура || '',
        quantity: Number(Количество) || 0,
      })
    }
  )

  return accounts
}

/**
 * `Result` of Mobile/Balances — one row per fuel of a wallet, or per batch
 * when asked with them, so the rows are summed by fuel either way. Fuels at
 * zero are dropped: they are not leftovers.
 */
export const balancesAdapter = (rows: any[]): IAccountFuel[] => {
  const byFuel: { [id: string]: IAccountFuel } = {}
  const result: IAccountFuel[] = []

  ;(rows || []).forEach(
    ({
      Номенклатура,
      НоменклатураНаименование,
      СокращенноеОбозначение,
      Количество,
    }) => {
      const id = Номенклатура || ''
      if (!byFuel[id]) {
        byFuel[id] = {
          id,
          name: НоменклатураНаименование || СокращенноеОбозначение || '',
          quantity: 0,
        }
        result.push(byFuel[id])
      }
      byFuel[id].quantity += Number(Количество) || 0
    }
  )

  return result.filter((fuel) => fuel.quantity > 0)
}

/**
 * `Result.fuelstationData` of Cabinet/GetStationConfiguration — one row per
 * nozzle. `Dispenser` goes back to FillUpCar as a string, so it is kept as one.
 */
export const nozzlesAdapter = (nozzles: any[]): INozzle[] =>
  (nozzles || []).map(
    ({ Dispenser, Nozzle, price, goodsGUID, goodsCode, goodsName }) => ({
      dispenser: String(Dispenser ?? ''),
      nozzle: String(Nozzle ?? ''),
      price: Number(price) || 0,
      goodsGuid: goodsGUID || '',
      goodsCode: String(goodsCode ?? ''),
      goodsName: goodsName || '',
    })
  )

/**
 * The same fuel under two spellings: FuelData calls it `НоменклатураУИД` and
 * GetStationConfiguration `goodsGUID`, and 1C is not consistent about case or
 * the surrounding braces. Compared normalised — never stored that way, since
 * `goodsGuid` goes back to FillUpCar exactly as the station gave it.
 */
export const normaliseGuid = (value?: string) =>
  (value || '').replace(/[{}\s]/g, '').toLowerCase()

export const sameGuid = (a?: string, b?: string) =>
  !!a && !!b && normaliseGuid(a) === normaliseGuid(b)

/** Distinct dispensers of a station configuration, ascending. */
export const getDispensers = (nozzles: INozzle[]): string[] =>
  Array.from(new Set(nozzles.map((item) => item.dispenser))).sort(
    (a, b) => Number(a) - Number(b) || a.localeCompare(b)
  )

/** The «Власний» wallet everyone gets — see SHARED_WALLET_GUID. */
export const isSharedWallet = (id: string) => sameGuid(id, SHARED_WALLET_GUID)

export const walletsAdapter = (wallets: any[]): IRefuelWallet[] =>
  (wallets || [])
    .filter((item) => item['Включен'] !== false)
    .map(({ Счет, СчетНаименование, КонтрагентНаименование }) => ({
      id: Счет || '',
      name: СчетНаименование || '',
      organization: КонтрагентНаименование || '',
    }))
    // The driver's own wallets first; the shared «Власний» closes the list.
    .sort((a, b) => Number(isSharedWallet(a.id)) - Number(isSharedWallet(b.id)))

export const walletBalanceAdapter = (accounts: IRefuelAccount[]): IFuelBalance[] => {
  const byFuel: { [id: string]: IFuelBalance } = {}
  const result: IFuelBalance[] = []

  accounts.forEach((account) =>
    account.fuels.forEach((fuel) => {
      if (!byFuel[fuel.id]) {
        byFuel[fuel.id] = { id: fuel.id, name: fuel.name, quantity: 0 }
        result.push(byFuel[fuel.id])
      }
      byFuel[fuel.id].quantity += fuel.quantity
    })
  )

  return result
}
